import React from "react"
import * as Yup from 'yup'
import Ajax from "../../../Ajax"
import { useFormik } from "formik"
import { observer } from "mobx-react-lite"
import Field from "../../form/fields/Field"
import { Alert, AlertModal } from "../../.."
import { store } from "../../../stores/Store"
import ViewQuestionnaire from "./ViewQuestionnaire"
import { Button } from "@mui/material"
import { SelectOption } from "../../form/fields/SelectField"
import { Questionnaire, Template } from "../../../models/Interfaces"
import FormFields from "./template_reader/FormFields"

interface CreateQuestionnaireFormProps {
    questionnaire?: Questionnaire;
    answers?: string;
    preloadedAnswers?: string;
}

interface Payload {
    advisorUid: string;
    displayName: string;
    templateUid: string;
    respondentEmail: string;
    notificationEmail: string;
    answers: string;
    preloadedAnswers: string;
}

const submit = async (values: Payload) => {
    values.respondentEmail = values.respondentEmail.toLowerCase()
        
    await Ajax.Questionnaire.Create(values).then((response) => {
        store.QuestionnaireStore.getQuestionnaires()
        store.AlertStore.Close()
        AlertModal({width: "700px", body: <ViewQuestionnaire uid={response.data?.uid} />})
    }).catch((response) => {
        Alert({message: response.data?.detail, color: "error"})
    })
}


export default observer(function CreateQuestionnaireForm ({questionnaire = null, answers = null, preloadedAnswers = null}: CreateQuestionnaireFormProps) {

    const userAccount = store.NodeStore.accounts.find((a) => a.uid === store.AppStore.sessionInfo.userAccount.uid)
    const initialValues: Payload = {
        advisorUid: questionnaire ? questionnaire.respondent.advisorUid : userAccount.advisorUid,
        displayName: questionnaire?.displayName,
        templateUid: questionnaire?.template.uid,
        respondentEmail: questionnaire?.respondent.respondentEmail,
        notificationEmail: store.AppStore.sessionInfo.userEmail,
        answers: answers,
        preloadedAnswers: preloadedAnswers
    }
    
    const [templateBody, setTemplateBody] = React.useState<any>(null)
    const [payload, setPayload] = React.useState<Payload>(initialValues)
    const [defaultSettings, setDefaultSettings] = React.useState<any>({})

    if (templateBody?.settings) {
        return (
            <div className="form">
                <h2>Create Questionnaire <span style={{fontWeight: 200, color: "grey"}}> - Settings</span></h2>

                <FormFields 
                    initialValues={defaultSettings}
                    fields={templateBody.settings.fields} 
                    onCancel={() => setTemplateBody(null)}
                    onSubmit={async (settingsAnswers) => {
                        var newPayload = {...payload, answers: JSON.stringify(settingsAnswers)}
                        setPayload(newPayload)
                        await submit(newPayload)
                    }} />
            </div>
        )
    }

    return (
        <MainForm 
            initialValues={payload}
            onSubmit={async (values: Payload) => {
                if (answers || preloadedAnswers) {
                    await submit(values)
                    return
                }

                var tBody = null
                
                await Ajax.QuestionnaireTemplate.Get(values.templateUid).then(response => {
                    tBody = JSON.parse(response.data.json)
                })

                if (tBody?.settings) {
                    await Ajax.QuestionnaireTemplate.GetDefaultSettings(values.templateUid, values.advisorUid).then((response) => {
                        if (typeof response.data?.settings === "object") {
                            setDefaultSettings(response.data?.settings)
                        }
                    })
                    
                    setPayload(values)
                    setTemplateBody(tBody)
                    return 
                }

                await submit(values)
            }} />
    )
})


function MainForm ({initialValues, onSubmit = async (values: Payload) => {}}) {

    const { templates, getTemplates, getQuestionnaires } = store.QuestionnaireStore
    const showAdviserUidField = !store.AppStore.checkJwtRoles(["client_account", "sponsor_account"], false)
    
    React.useEffect(() => {
        if (!templates?.length) {
            getTemplates()
        }
    }, [])

    const formik = useFormik({

        initialValues: initialValues,

        validationSchema: Yup.object({
            advisorUid: Yup.string().required("This field is required"),
            displayName: Yup.string().matches(/^[ ,.@!$%#';:?()[\]~_+=À-Ö\p{Sc}\p{So}\p{Mn}\p{P}\p{Z}À-ÿ\w\n]*$/u, "You have used some illegal characters").required("This field is required"),
            templateUid: Yup.string().required("This field is required"),
            respondentEmail: Yup.string().matches(
                /\b[\w\.-]+@[\w\.-]+\.\w{2,7}\b/,
                "A valid email is required"
            ).required("This field is required"),
        }),

        onSubmit: async (values, actions) => {
            await onSubmit(values)
        }
    })

    var templateOptions = Array.isArray(templates) && templates.length ? (
        [...templates].sort((a, b) => a.displayName.localeCompare(b.displayName)).filter(t => t.active).map((template) => {
            return {value: template.uid, label: template.displayName, help: template.version}
        }) 
    ) : []

    var clientOptions: SelectOption[] = []
    // Add customer accounts
    store.NodeStore.accounts.filter((a) => !!a.advisorUid).forEach((a) => {
        clientOptions.push({value: a.advisorUid, label: a.label})
    })

    return (
        <form className="form" onSubmit={formik.handleSubmit}>
            <div>
                <h2>Create Questionnaire</h2>
                <p>Complete this form to create a new questionnaire for your client.</p>
                <p>Once the questionnaire is created, you will be provided with a URL which you can send to the intended respondent.</p>
                {initialValues.answers || initialValues.preloadedAnswers ? <p>Answers from the existing Questionnaire will be copied.</p> : null}
            </div>

            <div className="form-fields" style={{maxWidth: "270px"}}>
                <Field.Text
                    formik
                    name="displayName"
                    label="Reference/label"
                    helpText="This can be the name of the applicant or job. The applicant won't see this."
                    onChange={formik.handleChange}
                    value={formik.values.displayName}
                    warning={formik.submitCount && formik.errors.displayName ? formik.errors.displayName : false} />

                <Field.Text
                    formik
                    name="respondentEmail"
                    label="Respondent contact email"
                    helpText="This is for your own record, we won't contact or share this email with anyone."
                    onChange={formik.handleChange}
                    value={formik.values.respondentEmail}
                    warning={formik.submitCount && formik.errors.respondentEmail ? formik.errors.respondentEmail : false} />
                
                <Field.Select
                    formik
                    label="Select a Questionnaire type"
                    name="templateUid"
                    disabled={!templates}
                    value={formik.values.templateUid}
                    warning={formik.submitCount && formik.errors.templateUid ? formik.errors.templateUid : ""}
                    options={templateOptions}
                    onChange={formik.handleChange} />

                {showAdviserUidField ? (
                    <Field.Select
                        formik
                        label="Assign to a Sponsor (optional)"
                        helpText="Enables filtering and allows authorised sponsor users to view this questionnaire."
                        name="advisorUid"
                        onChange={formik.handleChange}
                        value={formik.values.advisorUid}
                        warning={formik.submitCount && formik.errors.advisorUid ? formik.errors.advisorUid : false}
                        options={clientOptions} />
                ) : null}
            </div>

            <div className="form-buttons">
                <Button 
                    className="btn btn-sm" 
                    onClick={() => store.AlertStore.Close()}>
                    Cancel
                </Button>
                
                <Button 
                    className="btn btn-sm" 
                    color="success" 
                    type="submit" 
                    variant="contained" 
                    disabled={formik.isSubmitting}>
                    Create
                </Button>
            </div>
        </form>
    )
}